import { mapGetters, mapActions } from 'vuex'
import moment from 'moment-timezone'

import SheetWrapper from '@/components/SheetWrapper/index'
import InfoBadge from '@/components/InfoBadge/index'
import MainButton from '@/components/MainButton/index'
import Card from '@/components/Card/index'
import PlainTextField from '@/components/PlainTextField/index'
import ResponseDialog from '@/components/ResponseDialog/index'

import {
  formatTimestamp,
  epochFormat,
  getDateNowUnix
} from '@/utils/dateHelpers'
import { findFirstGameOfRound } from '@/utils/roundHelpers'
import { formatRank } from '@/utils/formatRank'
import {
  matchStatusTransformer,
  reverseFormeArray
} from '@/utils/matchStatusTransformer'

export default {
  name: 'predictions',
  components: {
    SheetWrapper,
    InfoBadge,
    MainButton,
    Card,
    PlainTextField,
    ResponseDialog
  },
  data: () => ({
    pointsBg: require('@/assets/points.svg'),
    totalPointsBg: require('@/assets/total-points.svg'),
    rankBg: require('@/assets/rank-ribbon.svg'),
    predictions: null,
    loading: false,
    predictionResponseDialog: false,
    predictionResponseDialogText: '',
    predictionInputDialog: false,
    predictionInputDialogText: 'You should only have 1-digit score prediction.',
    duration: null,
    durationInterval: null,
    lastWeekPoints: null,
    totalPoints: null,
    globalRanking: null,
    userScoreDetailsLoader: false,
    seasonStatusMessage: '',
    confirmDialog: false,
    confirmDialogText: ''
  }),
  computed: {
    ...mapGetters('predictions', {
      fetchFixturesResponse: 'FIXTURES',
      savePredictionsResponse: 'SAVE_PREDICTION_RESPONSE',
      fetchUserStatusResponse: 'USER_STATUS'
    }),
    nextWeekTitle() {
      return `${this.duration ? 'Next Round ' : 'Current'} Predictions`
    },
    countdown() {
      const remainingTime = this.duration && this.duration._milliseconds
      const formattedTime = epochFormat({
        unit: 'milliseconds',
        format: 'd[d] h[hr] m[m] s[s]',
        epochTime: remainingTime
      })
      return this.duration
        ? `Will close in ${formattedTime}`
        : `Predictions are now closed.`
    }
  },
  methods: {
    ...mapActions('predictions', {
      fetchFixtures: 'FETCH_FIXTURES',
      savePredictionsAction: 'SAVE_PREDICTION',
      fetchUserStatus: 'FETCH_USER_STATUS'
    }),
    bankered(state) {
      return state ? 'primary' : 'light'
    },
    toggleBanker(index) {
      this.predictions[index].banker = !this.predictions[index].banker
      this.$forceUpdate()
    },
    toggleBankerHandler(index) {
      const bankerCounter = this.predictions.filter(item => item.banker)
      if (bankerCounter.length < 3) {
        this.toggleBanker(index)
      } else if (bankerCounter.length === 3 && this.predictions[index].banker) {
        this.toggleBanker(index)
      } else {
        this.predictionResponseDialogText =
          'You can only have 3 bankers per round'
        this.predictionResponseDialog = true
      }
    },
    predictionInputOnChange(data) {
      if ((data || '').length > 1) {
        this.predictionInputDialog = true
      }
    },
    savePredictions() {
      const blankPredictions = this.predictions.filter(prediction => {
        if (prediction.statusShort === 'PST') {
          // Uncomment to enable postponed restrictions
          return false
          // return (
          //   prediction.awayTeam.prediction === 'null' ||
          //   prediction.awayTeam.prediction === '' ||
          //   (prediction.homeTeam.prediction === 'null' ||
          //     prediction.homeTeam.prediction === '')
          // )
        }
        return (
          prediction.awayTeam.prediction === 'null' ||
          prediction.awayTeam.prediction === '' ||
          (prediction.homeTeam.prediction === 'null' ||
            prediction.homeTeam.prediction === '')
        )
      })
      const bankerCounter = this.predictions.filter(item => item.banker)
      const confirmMessage = `Please take note that:${
        bankerCounter.length < 3 ? `\n* You have less than 3 bankers.` : ''
      }${
        blankPredictions.length > 0
          ? `\n* You have left some predictions blank.`
          : ''
      }
      \nDo you want to continue?`

      if (bankerCounter.length < 3 || blankPredictions.length > 0) {
        this.confirmDialogText = confirmMessage
        this.confirmDialog = true
      } else {
        this.confirmDialogButtonClick()
      }
    },
    predicted(value, index, key) {
      this.predictions[index][key].prediction = value || ''
      const predictionArray = this.$refs.prediction

      const predictionFocusedIndex = predictionArray.findIndex(
        item => item.$children[0].isFocused
      )
      const nextFieldIndex = predictionFocusedIndex + 1

      if (
        value &&
        nextFieldIndex < predictionArray.length &&
        value !== 'null'
      ) {
        predictionArray[nextFieldIndex].$children[0].focus()
      }
    },
    formatDate(timestamp) {
      const { original, local, dayDiff, isSame, numSign } = formatTimestamp({
        timestamp
      })
      const localTimeDisplay = isSame
        ? ''
        : `(${local}) ${dayDiff === 0 ? '' : `${numSign + dayDiff}`}`

      return `${original} UK ${localTimeDisplay}`
    },
    getFormeColor(forme) {
      return matchStatusTransformer(forme)
    },
    responseDialogButtonClick() {
      this.predictionResponseDialog = false
    },
    predictionInputDialogButtonClick() {
      this.predictionInputDialog = false
    },
    displayRank(rank) {
      return rank ? `${formatRank(rank)} Place` : 'No Place yet'
    },
    confirmDialogButtonClick() {
      this.confirmDialog = false
      const moreThan5Points = this.predictions.filter(prediction => {
        return (
          parseInt(prediction.homeTeam.prediction) +
            parseInt(prediction.awayTeam.prediction) >=
          5
        )
      })
      if (moreThan5Points.length <= 2) {
        this.loading = true
        // Uncomment to enable postponed restrictions
        const filterPredictions = this.predictions.filter(
          x => x.statusShort !== 'PST'
        )
        // const filterPredictions = this.predictions
        this.savePredictionsAction({
          prediction: filterPredictions
        }).then(() => {
          this.loading = false
          this.predictionResponseDialogText = 'Predictions saved'
          this.predictionResponseDialog = true
        })
      } else {
        this.predictionResponseDialogText =
          'You can only enter a total of 5 goals in a maximum of 2 games'
        this.predictionResponseDialog = true
      }
    },
    confirmDialogDeclineButtonClick() {
      this.confirmDialog = false
    },
    reverseForme(formeArray) {
      return reverseFormeArray(formeArray)
    },
    postponedGame(status) {
      // Uncomment next line to apply postponed game restrictions and display
      return status === 'PST'
      // return false
    }
  },
  activated() {
    this.userScoreDetailsLoader = true

    this.fetchFixtures().then(() => {
      const { data, seasonStatus } = this.fetchFixturesResponse
      const { status, message } = seasonStatus

      if (status) {
        // Uncomment next line to find the first game of round which is not postponed
        const firstGame = findFirstGameOfRound(data)
        this.predictions = data.map(prediction => {
          return {
            ...prediction,
            hasForme:
              prediction.homeTeam.forme.length ||
              prediction.awayTeam.forme.length
          }
        })
        this.seasonStatusMessage = ''
        // FORMAT ALL TIME TO MILLISECONDS
        const closingTime = parseInt(firstGame.event_timestamp) * 1000
        this.durationInterval = setInterval(() => {
          const currentTime = getDateNowUnix() * 1000
          const diffTime = closingTime - currentTime

          if (diffTime > 0) {
            const momentDiffTime = moment.duration(diffTime, 'milliseconds')
            this.duration = moment.duration(
              momentDiffTime - 1000,
              'milliseconds'
            )
          } else {
            clearInterval(this.durationInterval)
            this.duration = null
          }
        }, 1000)
      } else {
        this.seasonStatusMessage = message
      }
    })

    this.fetchUserStatus()
      .then(() => {
        const {
          data: {
            lastWeekPoints: { lastWeekPoints },
            totalAndGlobalRank
          }
        } = this.fetchUserStatusResponse

        this.lastWeekPoints = lastWeekPoints || 0
        const totalGlobalRanking = totalAndGlobalRank || 0

        const globalRanking = totalGlobalRanking
          ? formatRank(parseInt(totalGlobalRanking.rank))
          : '---'
        this.globalRanking = globalRanking

        const totalPoints = totalGlobalRanking
          ? totalGlobalRanking.total || 0
          : totalGlobalRanking
        this.totalPoints = totalPoints
      })
      .finally(() => {
        this.userScoreDetailsLoader = false
      })
  }
}
